import { useState, useEffect, useRef, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import TiptapEditor from '@/components/base/TiptapEditor';
import ImageUpload from '@/components/base/ImageUpload';
import FAQBuilder from '@/components/base/FAQBuilder';
import { supabase } from '@/lib/supabase';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image_url: string;
  author: string;
  category: string;
  is_published: boolean;
  published_at: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  faq_data: { question: string; answer: string }[];
}

interface EditorForm {
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  content: string;
  featured_image_url: string;
  author: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  is_published: boolean;
  faq_enabled: boolean;
  faq_data: { question: string; answer: string }[];
}

const emptyForm: EditorForm = {
  title: '',
  slug: '',
  excerpt: '',
  category: 'General',
  content: '',
  featured_image_url: '',
  author: 'Thornhill Drug-Mart',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  is_published: false,
  faq_enabled: false,
  faq_data: [
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' },
  ],
};

export default function AdminBlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState<EditorForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formRef = useRef(form);
  formRef.current = form;
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const isNewRef = useRef(isNew);
  isNewRef.current = isNew;

  const fetchPosts = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('blog_posts')
      .select('*')
      .order('published_at', { ascending: false });
    setPosts(data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // Autosave every 15 seconds
  useEffect(() => {
    if (!isNew && !editing) return;

    const doAutosave = async () => {
      const currentForm = formRef.current;
      const currentEditing = editingRef.current;

      if (!currentForm.title.trim()) return;

      setSaveStatus('saving');
      try {
        const saveData = {
          title: currentForm.title,
          slug: currentForm.slug,
          excerpt: currentForm.excerpt,
          content: currentForm.content,
          featured_image_url: currentForm.featured_image_url,
          author: currentForm.author,
          category: currentForm.category,
          meta_title: currentForm.meta_title,
          meta_description: currentForm.meta_description,
          meta_keywords: currentForm.meta_keywords,
          is_published: currentForm.is_published,
          faq_data: currentForm.faq_enabled ? currentForm.faq_data : [],
          updated_at: new Date().toISOString(),
        };

        if (isNewRef.current) {
          const { data, error } = await supabase
            .from('blog_posts')
            .insert({ ...saveData, slug: generateSlug(currentForm.title), published_at: new Date().toISOString() })
            .select('id')
            .single();
          if (error) throw error;
          if (data) {
            setEditing({ ...saveData, id: data.id, published_at: new Date().toISOString() } as BlogPost);
            setIsNew(false);
          }
        } else if (currentEditing) {
          const { error } = await supabase
            .from('blog_posts')
            .update(saveData)
            .eq('id', currentEditing.id);
          if (error) throw error;
        }

        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
        fetchPosts();
      } catch {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    };

    autosaveTimerRef.current = setInterval(doAutosave, 15000);

    return () => {
      if (autosaveTimerRef.current) clearInterval(autosaveTimerRef.current);
    };
  }, [isNew, editing]);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (value: string) => {
    setForm((prev) => ({ ...prev, title: value, slug: generateSlug(value) }));
  };

  const handleEdit = (post: BlogPost) => {
    setEditing(post);
    setIsNew(false);
    const faqs = (post as any).faq_data;
    const faqArr = Array.isArray(faqs) && faqs.length > 0 ? faqs : emptyForm.faq_data;
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || '',
      category: post.category,
      content: post.content,
      featured_image_url: post.featured_image_url || '',
      author: post.author,
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      is_published: post.is_published,
      faq_enabled: Array.isArray(faqs) && faqs.length > 0,
      faq_data: faqArr,
    });
    setValidationErrors([]);
  };

  const handleNew = () => {
    setEditing(null);
    setIsNew(true);
    setForm(emptyForm);
    setValidationErrors([]);
  };

  const handleCancel = () => {
    if (autosaveTimerRef.current) clearInterval(autosaveTimerRef.current);
    setEditing(null);
    setIsNew(false);
    setForm(emptyForm);
    setValidationErrors([]);
  };

  const validate = (): string[] => {
    const errors: string[] = [];
    if (!form.title.trim()) errors.push('Title is required.');
    if (!form.featured_image_url) errors.push('Featured image is required.');
    if (!form.content.trim() || form.content === '<p></p>') errors.push('Content cannot be empty.');
    if (!form.meta_title.trim()) errors.push('Meta title is required for SEO.');
    if (!form.meta_description.trim()) errors.push('Meta description is required for SEO.');
    return errors;
  };

  const handlePublish = async () => {
    const errors = validate();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setMessage({ type: 'error', text: 'Please fix validation errors before publishing.' });
      return;
    }
    setValidationErrors([]);
    setSaving(true);
    setMessage(null);

    try {
      const saveData = {
        title: form.title,
        slug: generateSlug(form.title),
        excerpt: form.excerpt,
        content: form.content,
        featured_image_url: form.featured_image_url,
        author: form.author,
        category: form.category,
        meta_title: form.meta_title,
        meta_description: form.meta_description,
        meta_keywords: form.meta_keywords,
        is_published: true,
        published_at: new Date().toISOString(),
        faq_data: form.faq_enabled ? form.faq_data : [],
        updated_at: new Date().toISOString(),
      };

      if (isNew) {
        const { error } = await supabase.from('blog_posts').insert(saveData);
        if (error) throw error;
      } else if (editing) {
        const { error } = await supabase.from('blog_posts').update(saveData).eq('id', editing.id);
        if (error) throw error;
      }

      setMessage({ type: 'success', text: 'Post published!' });
      handleCancel();
      fetchPosts();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
    setSaving(false);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const saveData = {
        title: form.title || 'Untitled Draft',
        slug: form.slug || generateSlug(form.title || 'draft'),
        excerpt: form.excerpt,
        content: form.content,
        featured_image_url: form.featured_image_url,
        author: form.author,
        category: form.category,
        meta_title: form.meta_title,
        meta_description: form.meta_description,
        meta_keywords: form.meta_keywords,
        is_published: false,
        faq_data: form.faq_enabled ? form.faq_data : [],
        updated_at: new Date().toISOString(),
      };

      if (isNew) {
        const { error } = await supabase.from('blog_posts').insert({ ...saveData, published_at: new Date().toISOString() });
        if (error) throw error;
      } else if (editing) {
        const { error } = await supabase.from('blog_posts').update(saveData).eq('id', editing.id);
        if (error) throw error;
      }

      setMessage({ type: 'success', text: 'Draft saved!' });
      handleCancel();
      fetchPosts();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this post permanently?')) return;
    await supabase.from('blog_posts').delete().eq('id', id);
    fetchPosts();
  };

  const handleTogglePublish = async (post: BlogPost) => {
    await supabase
      .from('blog_posts')
      .update({ is_published: !post.is_published, updated_at: new Date().toISOString() })
      .eq('id', post.id);
    fetchPosts();
  };

  const inputClass = 'w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm outline-none transition';
  const labelClass = 'block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1.5';

  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground-950">Blog Management</h1>
            <p className="text-sm text-foreground-600 mt-1">Create and manage blog posts</p>
          </div>
          {!isNew && !editing && (
            <button
              onClick={handleNew}
              className="bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-add-line"></i>
              New Post
            </button>
          )}
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-accent-100 border border-accent-200'
          }`}>
            <i className={`${message.type === 'success' ? 'ri-check-line text-green-600' : 'ri-error-warning-line text-accent-600'} text-lg`}></i>
            <p className="text-sm font-medium">{message.text}</p>
          </div>
        )}

        {/* Editor */}
        {(isNew || editing) && (
          <div className="space-y-6">
            {/* Card 1: Basic Information */}
            <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-background-100 flex items-center gap-2">
                <i className="ri-file-text-line text-foreground-500"></i>
                <h2 className="text-sm font-bold text-foreground-900 uppercase tracking-wider">Basic Information</h2>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <label className={labelClass}>Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Enter post title..."
                    className={`${inputClass} ${validationErrors.includes('Title is required.') ? 'border-accent-400 ring-2 ring-accent-200' : ''}`}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>Slug</label>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value })}
                      className={inputClass}
                    />
                    <p className="text-[10px] text-foreground-500 mt-1">Auto-generated from title. Edit if needed.</p>
                  </div>
                  <div>
                    <label className={labelClass}>Category</label>
                    <input
                      type="text"
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Excerpt</label>
                  <textarea
                    value={form.excerpt}
                    onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                    rows={2}
                    className={inputClass}
                    placeholder="A short summary of the post..."
                  />
                </div>
                <div>
                  <label className={labelClass}>Author</label>
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    className={`${inputClass} max-w-xs`}
                  />
                </div>
              </div>
            </div>

            {/* Card 2: Featured Image */}
            <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-background-100 flex items-center gap-2">
                <i className="ri-image-line text-foreground-500"></i>
                <h2 className="text-sm font-bold text-foreground-900 uppercase tracking-wider">Featured Image</h2>
              </div>
              <div className="p-6">
                {validationErrors.includes('Featured image is required.') && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
                    <i className="ri-error-warning-line text-accent-500 text-sm"></i>
                    <p className="text-xs text-accent-700">Please upload a featured image before publishing.</p>
                  </div>
                )}
                <ImageUpload
                  currentUrl={form.featured_image_url}
                  onUpload={(url) => setForm({ ...form, featured_image_url: url })}
                  onDelete={() => setForm({ ...form, featured_image_url: '' })}
                />
              </div>
            </div>

            {/* Card 3: Content */}
            <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-background-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-article-line text-foreground-500"></i>
                  <h2 className="text-sm font-bold text-foreground-900 uppercase tracking-wider">Content</h2>
                </div>
                {/* Save status indicator */}
                <div className="flex items-center gap-2">
                  {saveStatus === 'saving' && (
                    <span className="text-[10px] text-foreground-500 flex items-center gap-1 animate-pulse">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Saving...
                    </span>
                  )}
                  {saveStatus === 'saved' && (
                    <span className="text-[10px] text-green-600 flex items-center gap-1">
                      <i className="ri-check-line"></i>
                      Saved
                    </span>
                  )}
                  {saveStatus === 'error' && (
                    <span className="text-[10px] text-accent-500 flex items-center gap-1">
                      <i className="ri-close-line"></i>
                      Save failed
                    </span>
                  )}
                </div>
              </div>
              <div className="p-6">
                {validationErrors.includes('Content cannot be empty.') && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
                    <i className="ri-error-warning-line text-accent-500 text-sm"></i>
                    <p className="text-xs text-accent-700">Content cannot be empty. Please write something.</p>
                  </div>
                )}
                <div className="tiptap-editor">
                  <TiptapEditor
                    content={form.content}
                    onChange={(html) => setForm({ ...form, content: html })}
                    placeholder="Start writing your post..."
                  />
                </div>
              </div>
            </div>

            {/* Card 4: SEO */}
            <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-background-100 flex items-center gap-2">
                <i className="ri-search-eye-line text-foreground-500"></i>
                <h2 className="text-sm font-bold text-foreground-900 uppercase tracking-wider">SEO</h2>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <label className={labelClass}>Meta Title *</label>
                  <input
                    type="text"
                    value={form.meta_title}
                    onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                    placeholder="SEO title (50-60 characters recommended)"
                    className={`${inputClass} ${validationErrors.includes('Meta title is required for SEO.') ? 'border-accent-400 ring-2 ring-accent-200' : ''}`}
                  />
                  <p className="text-[10px] text-foreground-500 mt-1">{form.meta_title.length}/60 characters</p>
                </div>
                <div>
                  <label className={labelClass}>Meta Description *</label>
                  <textarea
                    value={form.meta_description}
                    onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                    rows={3}
                    placeholder="SEO description (120-160 characters recommended)"
                    className={`${inputClass} ${validationErrors.includes('Meta description is required for SEO.') ? 'border-accent-400 ring-2 ring-accent-200' : ''}`}
                  />
                  <p className="text-[10px] text-foreground-500 mt-1">{form.meta_description.length}/160 characters</p>
                </div>
                <div>
                  <label className={labelClass}>Meta Keywords</label>
                  <input
                    type="text"
                    value={form.meta_keywords}
                    onChange={(e) => setForm({ ...form, meta_keywords: e.target.value })}
                    placeholder="keyword1, keyword2, keyword3"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* Card 5: FAQ */}
            <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-background-100 flex items-center gap-2">
                <i className="ri-question-answer-line text-foreground-500"></i>
                <h2 className="text-sm font-bold text-foreground-900 uppercase tracking-wider">FAQ</h2>
              </div>
              <div className="p-6">
                <FAQBuilder
                  faqs={form.faq_data}
                  enabled={form.faq_enabled}
                  onToggle={(enabled) => setForm({ ...form, faq_enabled: enabled })}
                  onChange={(faqs) => setForm({ ...form, faq_data: faqs })}
                />
              </div>
            </div>

            {/* Card 6: Publish */}
            <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-background-100 flex items-center gap-2">
                <i className="ri-send-plane-line text-foreground-500"></i>
                <h2 className="text-sm font-bold text-foreground-900 uppercase tracking-wider">Publish</h2>
              </div>
              <div className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                      form.is_published
                        ? 'bg-green-100 text-green-700'
                        : 'bg-background-100 text-foreground-500'
                    }`}>
                      <i className={`${form.is_published ? 'ri-checkbox-circle-fill' : 'ri-draft-line'} text-sm`}></i>
                      {form.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={saving}
                      className="border border-background-200 text-foreground-700 hover:bg-background-50 font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer flex items-center gap-2"
                    >
                      <i className="ri-save-line"></i>
                      {saving ? 'Saving...' : 'Save Draft'}
                    </button>
                    <button
                      type="button"
                      onClick={() => window.open(`/blog/${form.slug}`, '_blank')}
                      disabled={!form.slug}
                      className="border border-background-200 text-foreground-600 hover:bg-background-50 font-semibold px-4 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer flex items-center gap-2"
                    >
                      <i className="ri-eye-line"></i>
                      Preview
                    </button>
                    <button
                      type="button"
                      onClick={handlePublish}
                      disabled={saving}
                      className="bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer flex items-center gap-2"
                    >
                      <i className="ri-rocket-line"></i>
                      {saving ? 'Publishing...' : 'Publish'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="text-foreground-500 hover:text-foreground-700 font-medium px-4 py-2.5 text-sm transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-foreground-500 mt-4">
                  <i className="ri-information-line mr-1"></i>
                  Changes are autosaved every 15 seconds. Use "Save Draft" to manually save without publishing.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Posts list */}
        {!(isNew || editing) && (
          <>
            {loading ? (
              <div className="text-center py-12 text-foreground-500">Loading posts...</div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 bg-background-50 border border-background-200 rounded-xl">
                <div className="w-12 h-12 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-article-line text-foreground-400 text-xl"></i>
                </div>
                <p className="text-foreground-600 font-medium">No blog posts yet</p>
                <p className="text-sm text-foreground-500 mt-1">Create your first post to get started</p>
              </div>
            ) : (
              <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-background-50 border-b border-background-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase">Title</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase hidden md:table-cell">Category</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase hidden md:table-cell">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase hidden lg:table-cell">Date</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-100">
                    {posts.map((post) => (
                      <tr key={post.id} className="hover:bg-background-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-foreground-900 truncate max-w-[200px]">{post.title}</p>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full font-medium">{post.category}</span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <button
                            onClick={() => handleTogglePublish(post)}
                            className={`text-xs font-medium px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap ${
                              post.is_published
                                ? 'bg-green-100 text-green-700'
                                : 'bg-background-100 text-foreground-500'
                            }`}
                          >
                            {post.is_published ? 'Published' : 'Draft'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-foreground-500 text-xs hidden lg:table-cell whitespace-nowrap">
                          {new Date(post.published_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleEdit(post)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer" title="Edit">
                              <i className="ri-edit-line text-foreground-500"></i>
                            </button>
                            <button onClick={() => handleDelete(post.id)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-accent-50 cursor-pointer" title="Delete">
                              <i className="ri-delete-bin-line text-accent-500"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}